import React, { useState, useEffect, useMemo } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { surveyTemplatesAPI } from '../../services/api';
import SurveyTemplateForm, { SurveyTemplateFormData } from '../../components/survey/SurveyTemplateForm';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import ActiveSurveyConflictModal from '../../components/ActiveSurveyConflictModal';

interface SurveyTemplate {
  id: string;
  name: string;
  description?: string;
  surveyType: string;
  active: boolean;
  emailTemplateId?: string | null;
  emailTemplateName?: string | null;
  createdAt: string;
  updatedAt: string;
  questions?: Array<{
    id: string;
    question: string;
    responseType: string;
    required: boolean;
    order: number;
    choices: string[];
  }>;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SurveyTemplatesManagement: React.FC = () => {
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SurveyTemplate | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<SurveyTemplate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState<SurveyTemplate | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loadingViewTemplate, setLoadingViewTemplate] = useState(false);
  const [showActiveConflictModal, setShowActiveConflictModal] = useState(false);
  const [activeSurveyName, setActiveSurveyName] = useState('');
  const [pendingFormData, setPendingFormData] = useState<SurveyTemplateFormData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingEditTemplateId, setPendingEditTemplateId] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [currentPage, pageSize]);

  // Reset to page 1 when page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await surveyTemplatesAPI.list({
        page: currentPage,
        limit: pageSize
      });
      if (response.data.success) {
        setTemplates(response.data.data);
        if (response.data.pagination) {
          setTotalCount(response.data.pagination.total);
          setTotalPages(response.data.pagination.totalPages);
        }
      }
    } catch (error: any) {
      console.error('Failed to fetch survey templates:', error);
      showNotification(error.response?.data?.message || 'Failed to load survey templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDeleteClick = (template: SurveyTemplate) => {
    // Prevent deletion of active survey templates
    if (template.active) {
      showNotification('Active Survey cannot be deleted. Please deactivate the survey first.', 'error');
      return;
    }
    
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    // Defense in depth: Check again if template is active before making API call
    if (templateToDelete.active) {
      showNotification('Active Survey cannot be deleted. Please deactivate the survey first.', 'error');
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      return;
    }

    try {
      setDeleting(true);
      await surveyTemplatesAPI.delete(templateToDelete.id);
      showNotification('Survey template deleted successfully', 'success');
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to delete survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to delete survey template', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const handleCreateTemplate = async (formData: SurveyTemplateFormData) => {
    try {
      setCreating(true);
      
      // If trying to create an active survey, check for conflicts first
      if (formData.active) {
        try {
          // Check if there's an active survey
          const activeSurveysResponse = await surveyTemplatesAPI.list({ active: true });
          if (activeSurveysResponse.data.success && activeSurveysResponse.data.data.length > 0) {
            const activeSurvey = activeSurveysResponse.data.data[0];
            // Show confirmation modal
            setActiveSurveyName(activeSurvey.name);
            setPendingFormData(formData);
            setShowActiveConflictModal(true);
            setCreating(false);
            return;
          }
        } catch (checkError: any) {
          console.error('Error checking for active surveys:', checkError);
          // Continue with creation if check fails
        }
      }

      // No conflict, proceed with creation
      await createSurveyTemplate(formData);
    } catch (error: any) {
      console.error('Failed to create survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to create survey template', 'error');
      setCreating(false);
      throw error;
    }
  };

  const createSurveyTemplate = async (formData: SurveyTemplateFormData, deactivateExistingId?: string) => {
    try {
      setCreating(true);
      
      // If deactivating existing survey, do it first
      if (deactivateExistingId) {
        try {
          const activeSurveysResponse = await surveyTemplatesAPI.list({ active: true });
          if (activeSurveysResponse.data.success && activeSurveysResponse.data.data.length > 0) {
            const activeSurvey = activeSurveysResponse.data.data[0];
            // Deactivate the existing active survey
            await surveyTemplatesAPI.update(activeSurvey.id, {
              name: activeSurvey.name,
              description: activeSurvey.description,
              active: false,
              emailTemplateId: activeSurvey.emailTemplateId || undefined,
              questions: activeSurvey.questions || []
            });
          }
        } catch (deactivateError: any) {
          console.error('Error deactivating existing survey:', deactivateError);
          showNotification('Failed to deactivate existing survey', 'error');
          throw deactivateError;
        }
      }

      // Create the new survey template
      const response = await surveyTemplatesAPI.create({
        name: formData.name,
        description: formData.description,
        // surveyType is always 'custom' (hidden from UI, set by backend)
        active: formData.active,
        emailTemplateId: formData.emailTemplateId,
        questions: formData.questions.map(q => ({
          question: q.question,
          responseType: q.responseType,
          required: q.required,
          choices: q.choices.filter(c => c.trim().length > 0),
          order: q.order
        }))
      });

      if (response.data.success) {
        showNotification('Survey template created successfully', 'success');
        setShowCreateModal(false);
        setShowActiveConflictModal(false);
        setPendingFormData(null);
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Failed to create survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to create survey template', 'error');
      throw error;
    } finally {
      setCreating(false);
    }
  };

  const handleActiveConflictConfirm = async () => {
    if (pendingFormData) {
      try {
        const activeSurveysResponse = await surveyTemplatesAPI.list({ active: true });
        if (activeSurveysResponse.data.success && activeSurveysResponse.data.data.length > 0) {
          const activeSurvey = activeSurveysResponse.data.data.find(
            (s: SurveyTemplate) => s.name === activeSurveyName
          );
          
          if (isEditMode && pendingEditTemplateId) {
            // Edit mode: deactivate existing and activate edited survey
            if (activeSurvey) {
              await updateSurveyTemplate(pendingEditTemplateId, pendingFormData, activeSurvey.id);
            } else {
              await updateSurveyTemplate(pendingEditTemplateId, pendingFormData);
            }
          } else {
            // Create mode: deactivate existing and create new survey
            if (activeSurvey) {
              await createSurveyTemplate(pendingFormData, activeSurvey.id);
            } else {
              await createSurveyTemplate(pendingFormData);
            }
          }
        } else {
          // No active survey found, proceed with operation
          if (isEditMode && pendingEditTemplateId) {
            await updateSurveyTemplate(pendingEditTemplateId, pendingFormData);
          } else {
            await createSurveyTemplate(pendingFormData);
          }
        }
      } catch (error: any) {
        console.error('Error handling active conflict:', error);
        showNotification(error.response?.data?.message || 'Failed to save survey template', 'error');
        if (isEditMode) {
          setUpdating(false);
        } else {
          setCreating(false);
        }
      }
    }
  };

  const handleActiveConflictCancel = () => {
    // Just close the modal without saving
    setShowActiveConflictModal(false);
    setPendingFormData(null);
    setPendingEditTemplateId(null);
    setIsEditMode(false);
    setActiveSurveyName('');
  };

  const handleSaveInactive = () => {
    // Save as inactive without deactivating existing active survey
    if (pendingFormData) {
      const inactiveFormData = { ...pendingFormData, active: false };
      
      if (isEditMode && pendingEditTemplateId) {
        // Edit mode: update as inactive
        updateSurveyTemplate(pendingEditTemplateId, inactiveFormData);
      } else {
        // Create mode: create as inactive
        createSurveyTemplate(inactiveFormData);
      }
    }
    setShowActiveConflictModal(false);
    setPendingFormData(null);
    setPendingEditTemplateId(null);
    setIsEditMode(false);
    setActiveSurveyName('');
  };

  const handleEditClick = async (templateId: string) => {
    try {
      setLoadingTemplate(true);
      const response = await surveyTemplatesAPI.get(templateId);
      if (response.data.success) {
        const templateData = response.data.data;
        // Debug logging to verify emailTemplateId is received
        console.log('Fetched survey template for editing:', {
          id: templateData.id,
          name: templateData.name,
          emailTemplateId: templateData.emailTemplateId,
          emailTemplateIdType: typeof templateData.emailTemplateId
        });
        setEditingTemplate(templateData);
        setShowEditModal(true);
      }
    } catch (error: any) {
      console.error('Failed to fetch survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to load survey template', 'error');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleViewClick = async (templateId: string) => {
    try {
      setLoadingViewTemplate(true);
      const response = await surveyTemplatesAPI.get(templateId);
      if (response.data.success) {
        setViewingTemplate(response.data.data);
        setShowViewModal(true);
      }
    } catch (error: any) {
      console.error('Failed to fetch survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to load survey template', 'error');
    } finally {
      setLoadingViewTemplate(false);
    }
  };

  const handleUpdateTemplate = async (formData: SurveyTemplateFormData) => {
    if (!editingTemplate) return;

    try {
      setUpdating(true);
      
      // If trying to activate an inactive survey, check for conflicts
      // Only check if the survey is currently inactive and user is trying to set it to active
      if (formData.active && !editingTemplate.active) {
        try {
          // Check if there's an active survey (excluding the current one being edited)
          const activeSurveysResponse = await surveyTemplatesAPI.list({ active: true });
          if (activeSurveysResponse.data.success && activeSurveysResponse.data.data.length > 0) {
            // Check if the active survey is different from the one being edited
            const otherActiveSurvey = activeSurveysResponse.data.data.find(
              (s: SurveyTemplate) => s.id !== editingTemplate.id
            );
            
            if (otherActiveSurvey) {
              // Show confirmation modal
              setActiveSurveyName(otherActiveSurvey.name);
              setPendingFormData(formData);
              setPendingEditTemplateId(editingTemplate.id);
              setIsEditMode(true);
              setShowActiveConflictModal(true);
              setUpdating(false);
              return;
            }
          }
        } catch (checkError: any) {
          console.error('Error checking for active surveys:', checkError);
          // Continue with update if check fails
        }
      }

      // No conflict, proceed with update
      await updateSurveyTemplate(editingTemplate.id, formData);
    } catch (error: any) {
      console.error('Failed to update survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to update survey template', 'error');
      setUpdating(false);
      throw error;
    }
  };

  const updateSurveyTemplate = async (templateId: string, formData: SurveyTemplateFormData, deactivateExistingId?: string) => {
    try {
      setUpdating(true);
      
      // If deactivating existing survey, do it first
      if (deactivateExistingId) {
        try {
          const activeSurveysResponse = await surveyTemplatesAPI.list({ active: true });
          if (activeSurveysResponse.data.success && activeSurveysResponse.data.data.length > 0) {
            const activeSurvey = activeSurveysResponse.data.data.find(
              (s: SurveyTemplate) => s.id === deactivateExistingId
            );
            if (activeSurvey) {
              // Deactivate the existing active survey
              await surveyTemplatesAPI.update(activeSurvey.id, {
                name: activeSurvey.name,
                description: activeSurvey.description,
                active: false,
                emailTemplateId: activeSurvey.emailTemplateId || undefined,
                questions: activeSurvey.questions || []
              });
            }
          }
        } catch (deactivateError: any) {
          console.error('Error deactivating existing survey:', deactivateError);
          showNotification('Failed to deactivate existing survey', 'error');
          throw deactivateError;
        }
      }

      // Update the survey template
      const response = await surveyTemplatesAPI.update(templateId, {
        name: formData.name,
        description: formData.description,
        // surveyType is always 'custom' (hidden from UI, set by backend)
        active: formData.active,
        emailTemplateId: formData.emailTemplateId,
        questions: formData.questions.map((q: any) => ({
          id: q.id, // Preserve existing question IDs
          question: q.question,
          responseType: q.responseType,
          required: q.required,
          choices: q.choices.filter((c: string) => c.trim().length > 0),
          order: q.order
        }))
      });

      if (response.data.success) {
        showNotification('Survey template updated successfully', 'success');
        setShowEditModal(false);
        setShowActiveConflictModal(false);
        setEditingTemplate(null);
        setPendingFormData(null);
        setPendingEditTemplateId(null);
        setIsEditMode(false);
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Failed to update survey template:', error);
      showNotification(error.response?.data?.message || 'Failed to update survey template', 'error');
      throw error;
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Survey Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage survey templates for creating surveys
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Template
        </button>
      </div>

      {notification && (
        <div
          className={`mb-4 rounded-md p-4 ${
            notification.type === 'success'
              ? 'bg-green-50 text-green-800'
              : 'bg-red-50 text-red-800'
          }`}
        >
          {notification.message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-500">
          No survey templates found. Create your first survey template to get started.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Email Template
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{template.name}</div>
                      {template.description && (
                        <div className="mt-1 text-sm text-gray-500">{template.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {template.emailTemplateName || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {template.active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewClick(template.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View template"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditClick(template.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit template"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(template)}
                          disabled={template.active}
                          className={`${
                            template.active 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title={template.active ? 'Active Survey cannot be deleted' : 'Delete template'}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination */}
          {!loading && templates.length > 0 && totalPages > 0 && (
            <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex-1 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-700">
                      Show
                    </label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">entries</span>
                  </div>

                  <div className="text-sm text-gray-700">
                    Showing {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
                  </div>
                </div>

                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">First</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                      </svg>
                    </button>

                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {(() => {
                      const pages: (number | string)[] = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible + 2) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);

                        if (currentPage > 3) {
                          pages.push('...');
                        }

                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);

                        for (let i = start; i <= end; i++) {
                          pages.push(i);
                        }

                        if (currentPage < totalPages - 2) {
                          pages.push('...');
                        }

                        pages.push(totalPages);
                      }

                      return pages.map((pageNum, index) => (
                        <React.Fragment key={index}>
                          {pageNum === '...' ? (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          ) : (
                            <button
                              onClick={() => setCurrentPage(pageNum as number)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          )}
                        </React.Fragment>
                      ));
                    })()}

                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>

                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Last</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02zm-6 0a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Template Modal */}
      <Transition appear show={showCreateModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCreateModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all flex flex-col max-h-[90vh]">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold text-gray-900"
                      >
                        Create Survey Template
                      </Dialog.Title>
                      <button
                        onClick={() => setShowCreateModal(false)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Create a new survey template with questions and response types
                    </p>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    <SurveyTemplateForm
                      onSubmit={handleCreateTemplate}
                      onCancel={() => setShowCreateModal(false)}
                      loading={creating}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Template Modal */}
      <Transition appear show={showEditModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {
          setShowEditModal(false);
          setEditingTemplate(null);
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold text-gray-900"
                      >
                        Edit Survey Template
                      </Dialog.Title>
                      <button
                        onClick={() => {
                          setShowEditModal(false);
                          setEditingTemplate(null);
                        }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Update survey template details and questions
                    </p>
                  </div>

                  <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                    {loadingTemplate ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                      </div>
                    ) : editingTemplate ? (
                      <SurveyTemplateForm
                        onSubmit={handleUpdateTemplate}
                        onCancel={() => {
                          setShowEditModal(false);
                          setEditingTemplate(null);
                        }}
                        loading={updating}
                        initialData={{
                          name: editingTemplate.name,
                          description: editingTemplate.description ?? '',
                          surveyType: editingTemplate.surveyType as any,
                          active: editingTemplate.active,
                          emailTemplateId: editingTemplate.emailTemplateId ?? '',
                          questions: (editingTemplate.questions ?? []).map(q => ({
                            id: q.id,
                            question: q.question,
                            responseType: q.responseType as any,
                            required: q.required,
                            choices: q.choices || [],
                            order: q.order
                          }))
                        }}
                      />
                    ) : null}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* View Template Modal */}
      <Transition appear show={showViewModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {
          setShowViewModal(false);
          setViewingTemplate(null);
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold text-gray-900"
                      >
                        View Survey Template
                      </Dialog.Title>
                      <button
                        onClick={() => {
                          setShowViewModal(false);
                          setViewingTemplate(null);
                        }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-left">
                      Preview how this survey template will appear
                    </p>
                  </div>

                  <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                    {loadingViewTemplate ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                      </div>
                    ) : viewingTemplate ? (
                      <SurveyTemplateForm
                        onSubmit={async () => {}} // No-op in view mode
                        onCancel={() => {
                          setShowViewModal(false);
                          setViewingTemplate(null);
                        }}
                        loading={false}
                        readOnly={true}
                        initialData={{
                          name: viewingTemplate.name,
                          description: viewingTemplate.description || '',
                          surveyType: viewingTemplate.surveyType as any,
                          active: viewingTemplate.active,
                          questions: (viewingTemplate.questions || []).map(q => ({
                            id: q.id,
                            question: q.question,
                            responseType: q.responseType as any,
                            required: q.required,
                            choices: q.choices || [],
                            order: q.order
                          }))
                        }}
                      />
                    ) : null}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      {templateToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Survey Template"
          itemName={templateToDelete.name}
          message={`Are you sure you want to delete the survey template "${templateToDelete.name}"? This will also delete all associated questions. This action cannot be undone.`}
          isLoading={deleting}
        />
      )}

      {/* Active Survey Conflict Modal */}
      <ActiveSurveyConflictModal
        isOpen={showActiveConflictModal}
        onClose={handleActiveConflictCancel}
        onConfirm={handleActiveConflictConfirm}
        onSaveInactive={handleSaveInactive}
        activeSurveyName={activeSurveyName}
        isLoading={creating || updating}
      />
    </div>
  );
};

export default SurveyTemplatesManagement;

